import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ServiceDiagnosticWizard from '../components/ServiceDiagnosticWizard';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Receipt as TaxIcon,
  AccountBalance as GstIcon,
  Gavel as McaIcon,
  BarChart as PlanningIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  ContactSupport,
  FlashOn
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

interface ServiceItem {
  id: string;
  name: string;
  features?: string[];
  popular?: boolean;
}

interface ServiceCategory {
  id: string;
  title: string;
  shortTitle: string;
  icon: React.ReactElement;
  description: string;
  services: ServiceItem[];
}

const Services: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialCat = searchParams.get('cat') || 'startups';

  const [activeTab, setActiveTab] = useState(initialCat);
  const [searchQuery, setSearchQuery] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  const serviceCategories: ServiceCategory[] = [
    {
      id: 'startups',
      title: 'Startups & Registration',
      shortTitle: 'Startups',
      icon: <BusinessIcon />,
      description: 'Complete business registration and startup incorporation services',
      services: [
        { id: 'private-company', name: 'Private Company Registration', popular: true, features: ['Certificate of Incorporation', '2 Class-3 DSCs', 'PAN & TAN', 'Name Approval'] },
        { id: 'public-company', name: 'Public Company Registration', features: ['7 Directors', 'Prospectus Drafting', 'CIN Allotment'] },
        { id: 'llp', name: 'Limited Liability Partnership (LLP) Registration', popular: true, features: ['LLPIN Allotment', 'Form 3 LLP Agreement', '2 Partner DSCs'] },
        { id: 'partnership', name: 'Partnership Firm Registration', features: ['Deed Drafting', 'ROF Registration', 'PAN Allotment'] },
        { id: 'huf', name: 'HUF Registration', features: ['HUF Deed', 'Karta PAN', 'Bank Account Support'] },
        { id: 'proprietor', name: 'Proprietor Registration', features: ['MSME / GST Proof', 'Bank Account Setup', 'Business PAN'] },
        { id: 'trademark', name: 'Trademark Registration', popular: true, features: ['™ Symbol Rights', 'Public Class Search', 'Form TM-A Filing'] },
        { id: 'dsc', name: 'Digital Signature Certificate', features: ['Class 3 Encryption', 'Aadhaar Video eKYC', 'USB Token'] },
        { id: 'startup-india', name: 'Startup Registration (DPIIT)', features: ['Tax Exemption 80-IAC', 'Self Certification', 'Government Recognition'] },
        { id: 'msme', name: 'MSME / Udyam Registration', features: ['Instant Certificate', 'Bank Loan Subsidy', 'Govt Tender Access'] }
      ]
    },
    {
      id: 'income-tax',
      title: 'Income Tax',
      shortTitle: 'Income Tax',
      icon: <TaxIcon />,
      description: 'Comprehensive income tax filing, notice responses and advisory services',
      services: [
        { id: 'tax-consultancy', name: 'Tax Consultancy', popular: true, features: ['Senior CA Consultation', 'Regime Optimization', 'Tax Planning'] },
        { id: 'basic-return', name: 'Basic Return Filing (ITR-1)', features: ['Single Form 16', 'House Property', 'Interest Income'] },
        { id: 'basic-business-return', name: 'Basic Business Return Filing (ITR-4)', features: ['Presumptive Sec 44AD', 'Freelancer Sec 44ADA'] },
        { id: 'business-return', name: 'Business Return Filing (ITR-3)', popular: true, features: ['F&O / Intraday Trading', 'Full Books Audit', 'P&L Statement'] },
        { id: 'capital-gain', name: 'Capital Gain Return Filing (ITR-2)', features: ['Real Estate Sales', 'Mutual Funds & Equity', 'Crypto Assets'] },
        { id: 'nri-return', name: 'NRI Return Filing', features: ['Double Tax Relief (DTAA)', 'Foreign Assets Disclosure', 'NRE/NRO Advice'] },
        { id: 'partnership-return', name: 'Partnership/LLP Return Filing (ITR-5)', features: ['Partner Remuneration', 'Interest on Capital', 'Form 5 Filing'] },
        { id: 'company-return', name: 'Company Return Filing (ITR-6)', popular: true, features: ['Corporate Tax Rate 22%', 'Tax Audit 44AB', 'MAT Calculation'] },
        { id: 'tax-notice', name: 'Income Tax Notice Response', features: ['143(1) & 139(9) Reply', 'Scrutiny Defense', 'Demand Appeal'] },
        { id: 'tds-return', name: 'TDS Return Filing', features: ['Form 24Q / 26Q', 'FVU Generation', 'Form 16/16A Issuance'] }
      ]
    },
    {
      id: 'tax-planning',
      title: 'Tax Planning',
      shortTitle: 'Tax Planning',
      icon: <PlanningIcon />,
      description: 'Strategic tax planning solutions for optimal legal tax savings',
      services: [
        { id: 'tax-plan-basic', name: 'Salaried Tax Optimization', features: ['Old vs New Regime', '80C/80D/HRA Strategy', 'NPS Tax Benefit'] },
        { id: 'tax-plan-standard', name: 'Freelancer & Consultant Tax Advisory', popular: true, features: ['Section 44ADA 50% Flat Tax', 'Expense Structuring', 'Advance Tax Plan'] },
        { id: 'tax-plan-premium', name: 'HNW Surcharge & Capital Gains Advisory', popular: true, features: ['Surcharge Mitigation', 'Section 54/54EC Real Estate', 'Wealth Management'] }
      ]
    },
    {
      id: 'gst',
      title: 'GST',
      shortTitle: 'GST',
      icon: <GstIcon />,
      description: 'GST registration, monthly return filings, and legal notice defense',
      services: [
        { id: 'gst-registration', name: 'GST Registration', popular: true, features: ['15-Digit GSTIN Allotment', 'Aadhaar eKYC', 'REG-06 Certificate'] },
        { id: 'gst-monthly-return', name: 'GST Monthly Return Filing', popular: true, features: ['GSTR-1 Sales Filing', 'GSTR-3B Tax Filing', 'GSTR-2B ITC Match'] },
        { id: 'gst-quarterly-return', name: 'GST Composition Quarterly Return', features: ['CMP-08 Filing', 'Low Flat Rate Tax', 'Turnover Summary'] },
        { id: 'gst-amendment', name: 'GST Amendment', features: ['Address Change', 'Director Addition', 'Core Field Update'] },
        { id: 'gst-cancellation', name: 'GST Cancellation', features: ['Surrender GSTIN', 'Final Stock Return', 'No Dues Certificate'] },
        { id: 'gst-annual-return', name: 'GST Annual Return (GSTR-9 / 9C)', features: ['Financial Reconciliation', 'Audit Certificate', 'GSTR-9 Submission'] },
        { id: 'gst-notice', name: 'GST Legal Notice / SCN Response', popular: true, features: ['ASMT-10 / DRC-01 Defense', 'ITC Mismatch Reply', 'Officer Representation'] },
        { id: 'gst-ca', name: 'GST CA Retainer (Yearly)', features: ['Complete Filing Outsourcing', 'Notice Defense', 'Quarterly Audit'] }
      ]
    },
    {
      id: 'mca',
      title: 'MCA & Compliances',
      shortTitle: 'MCA',
      icon: <McaIcon />,
      description: 'Company law and statutory regulatory compliance services',
      services: [
        { id: 'company-annual-compliance', name: 'Company Annual Compliance', popular: true, features: ['Form AOC-4 Financials', 'Form MGT-7 Annual Return', 'DIR-3 KYC'] },
        { id: 'llp-annual-compliance', name: 'LLP Annual Compliance', features: ['Form 11 Annual Return', 'Form 8 Statement of Accounts'] },
        { id: 'din-ekyc', name: 'DIR-3 Director eKYC', popular: true, features: ['OTP Verification', 'Avoid ₹5,000 Penalty', 'DIN Reactivation'] },
        { id: 'ca-consultancy', name: 'CS & CA Legal Consultancy', features: ['1-on-1 Consultation', 'Corporate Structuring', 'Board Resolutions'] },
        { id: 'name-change', name: 'Company Name Change', features: ['MGT-14 Filing', 'RUN Application', 'Altered MOA/AOA'] },
        { id: 'register-office-change', name: 'Registered Office Change', features: ['Form INC-22', 'State / ROC Shifting', 'Utility NOC'] },
        { id: 'director-change', name: 'Director Addition / Removal', features: ['Form DIR-12', 'Board Resolution', 'DIR-2 Consent'] },
        { id: 'dpt3-filing', name: 'DPT-3 Outstanding Loan Filing', features: ['Auditor Certificate', 'Loan Return', 'Annual Mandatory'] },
        { id: 'adt1-filing', name: 'ADT-1 Auditor Appointment', features: ['AGM Resolution', '5-Year Appointment', 'Form ADT-1'] },
        { id: 'authorised-capital-increase', name: 'Authorised Capital Increase', features: ['Form SH-7', 'Stamp Duty Receipt', 'EGM Minutes'] },
        { id: 'bookkeeping', name: 'Bookkeeping & Accounting', features: ['Monthly Tally/Zoho', 'Bank Reconciliation', 'Financial Statements'] }
      ]
    }
  ];

  const activeCategory = serviceCategories.find(cat => cat.id === activeTab) || serviceCategories[0];

  const filteredServices = activeCategory.services.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (service.features?.some(f => f.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  return (
    <Box>
      <SEOHead
        title="Our Services | Tax Filing, GST, Startup Registration | Fingard Partners"
        description="Explore Fingard Partners' comprehensive services — Income Tax Filing, GST Registration & Returns, Startup Incorporation, MCA Compliance, and Tax Planning."
        keywords="Tax Filing Services, GST Registration, Startup Registration India, MCA Compliance, Tax Planning Services"
        canonicalUrl="/services"
      />
      {/* Breadcrumb */}
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ fontSize: '0.875rem' }}
        >
          <MuiLink
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
            Home
          </MuiLink>
          <Typography color="text.primary">Services</Typography>
          <Typography color="text.primary" fontWeight={600}>{activeCategory.title}</Typography>
        </Breadcrumbs>
      </Container>

      {/* Hero Banner */}
      <Box
        sx={{
          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 5, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Chip label="Master Services Portal" color="secondary" size="small" sx={{ mb: 2, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.5px' }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1.5, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.2 }}>
            {activeCategory.title}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 600, mb: 3 }}>
            {activeCategory.description}
          </Typography>
          
          <Button 
            component={RouterLink} 
            to={activeCategory.id === 'startups' ? '/startup-registration' : activeCategory.id === 'income-tax' ? '/itr-calculator' : activeCategory.id === 'gst' ? '/gst-services' : activeCategory.id === 'tax-planning' ? '/tax-planning' : '/mca-compliance'} 
            variant="contained" 
            color="secondary" 
            endIcon={<NavigateNextIcon />}
            sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
          >
            Start {activeCategory.shortTitle} Diagnostic
          </Button>
        </Container>
      </Box>

      {/* Main Layout */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={4} alignItems="flex-start">

          {/* —— Sidebar —— */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                position: { md: 'sticky' },
                top: { md: 24 }
              }}
            >
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="overline" sx={{ fontWeight: 700, fontSize: '0.65rem', letterSpacing: '1px', color: 'text.secondary' }}>
                  Categories
                </Typography>
              </Box>

              <List disablePadding>
                {serviceCategories.map((category, index) => {
                  const isActive = category.id === activeTab;
                  return (
                    <React.Fragment key={category.id}>
                      {index > 0 && <Divider />}
                      <ListItemButton
                        selected={isActive}
                        onClick={() => { setActiveTab(category.id); setSearchQuery(''); }}
                        sx={{
                          py: 1.5,
                          px: 2.5,
                          gap: 1.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.50',
                            borderLeft: '3px solid',
                            borderColor: 'primary.main',
                            pl: `calc(20px - 3px)`,
                            '&:hover': { bgcolor: 'primary.50' }
                          },
                          '&:not(.Mui-selected)': {
                            borderLeft: '3px solid transparent',
                            pl: `calc(20px - 3px)`,
                          }
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            color: isActive ? 'primary.main' : 'text.secondary',
                            '& .MuiSvgIcon-root': { fontSize: '1.1rem' }
                          }}
                        >
                          {category.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={category.shortTitle}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? 'primary.main' : 'text.primary',
                            noWrap: true
                          }}
                        />
                        <Chip
                          label={category.services.length}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: isActive ? 'primary.main' : 'grey.100',
                            color: isActive ? 'white' : 'text.secondary',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>

              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Need a custom solution?
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  fullWidth
                  component={RouterLink}
                  to="/contact"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                >
                  Talk to a Senior CA
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* —— Main Content Services Grid —— */}
          <Grid item xs={12} md={9}>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Box sx={{ color: 'primary.main', display: 'flex', '& .MuiSvgIcon-root': { fontSize: '1.3rem' } }}>
                  {activeCategory.icon}
                </Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                  {activeCategory.title}
                </Typography>
                <Chip
                  label={`${activeCategory.services.length} services`}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'primary.50', color: 'primary.main' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {activeCategory.description}
              </Typography>
            </Box>

            {/* Search Input */}
            <Paper
              component="div"
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                '&:focus-within': { borderColor: 'primary.main', boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}22` }
              }}
            >
              <SearchIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
              <TextField
                fullWidth
                placeholder={`Search in ${activeCategory.shortTitle}...`}
                variant="standard"
                size="small"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '0.9rem', py: 0.75 }
                }}
              />
              {searchQuery && (
                <Button size="small" onClick={() => setSearchQuery('')} sx={{ minWidth: 'auto', color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem' }}>
                  Clear
                </Button>
              )}
            </Paper>

            {/* Services List */}
            {filteredServices.length === 0 ? (
              <Paper
                elevation={0}
                sx={{ p: 5, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 3 }}
              >
                <SearchIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>No services found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No results for "{searchQuery}"
                </Typography>
                <Button variant="text" color="primary" onClick={() => setSearchQuery('')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Clear search
                </Button>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredServices.map((service) => (
                  <Card
                    key={service.id}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: service.popular ? '#123D6B' : '#E2E8F0',
                      borderLeft: service.popular ? '4px solid' : '1px solid',
                      borderLeftColor: service.popular ? '#C59B27' : '#E2E8F0',
                      borderRadius: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#123D6B',
                        boxShadow: '0 4px 20px rgba(18,61,107,0.08)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 2,
                        p: { xs: 2.5, md: 3 }
                      }}
                    >
                      {/* Service Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: service.features?.length ? 1 : 0 }}>
                          <Typography
                            component={RouterLink}
                            to={`/service/${service.id}`}
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#123D6B', textDecoration: 'none', '&:hover': { color: '#C59B27' } }}
                          >
                            {service.name}
                          </Typography>
                          {service.popular && (
                            <Chip
                              label="Popular"
                              size="small"
                              sx={{ bgcolor: '#C59B27', color: '#FFFFFF', height: 20, fontSize: '0.65rem', fontWeight: 700, '& .MuiChip-label': { px: 1 } }}
                            />
                          )}
                        </Box>

                        {service.features && service.features.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
                            {service.features.map((feature, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  bgcolor: '#F1F5F9',
                                  borderRadius: 1.5,
                                  px: 1.2,
                                  py: 0.4
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: '0.75rem', color: '#10B981' }} />
                                <Typography variant="caption" sx={{ color: '#334155', fontWeight: 500 }}>
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* CTA Buttons */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flexShrink: 0,
                          width: { xs: '100%', sm: 'auto' },
                          pt: { xs: 1.5, sm: 0 },
                          borderTop: { xs: '1px solid', sm: 'none' },
                          borderColor: 'divider'
                        }}
                      >
                        <Button
                          variant="contained"
                          disableElevation
                          size="small"
                          component={RouterLink}
                          to={`/service/${service.id}`}
                          endIcon={<FlashOn sx={{ fontSize: '0.9rem !important' }} />}
                          sx={{
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            px: 3,
                            py: 1,
                            bgcolor: '#123D6B',
                            color: '#FFFFFF',
                            '&:hover': { bgcolor: '#C59B27' }
                          }}
                        >
                          View Details & Quiz →
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#F1F5F9', py: 8, mt: 6, borderTop: '1px solid #E2E8F0' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 2, color: '#123D6B' }}>
            Need Custom Corporate Advisory?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            Our Senior Partner Chartered Accountants and Company Secretaries provide custom retainer advisory for multi-state enterprises.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/contact"
            startIcon={<ContactSupport />}
            sx={{ borderRadius: 2.5, px: 4, py: 1.5, textTransform: 'none', fontWeight: 700, bgcolor: '#123D6B', '&:hover': { bgcolor: '#C59B27' } }}
          >
            Talk to an Expert Senior CA
          </Button>
        </Container>
      </Box>

      {/* Quick Service Diagnostic Modal */}
      <ServiceDiagnosticWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        serviceCategory={activeTab}
        initialServiceName=""
      />
    </Box>
  );
};

export default Services;
